import { useCallback, useMemo, useState } from 'react'
import { ENTITY_TYPES } from '@sales-center/common'
import { toast } from '@sales-center/components'
import { post } from '@user/store/services/crud'
import { AddFormResponse } from '@user/store/agreements/typings'
import { AgreementSource, CreateAgreementFormModel } from '../../typings'
import { ServiceConfig } from '@user/store/services/config'
import { updateLeadAgreementsCount } from '@user/store/lead/slice'
import { updateOpportunityAgreementsCount } from '@user/store/opportunity/slice'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { FormikErrors } from 'formik/dist/types'
import { useSelector } from 'react-redux/es/hooks/useSelector'
import { selectSelectedTemplate } from '@user/store/agreements/slice'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts/slice'
import { useData } from '@user/features/EntityDetails/hooks'

type Params = {
  entityId: number
  entityType: ENTITY_TYPES
  externalLocationId: string
  onClose: () => void
  source?: AgreementSource
}

export function useActions(params: Params) {
  const {
    entityId,
    entityType,
    externalLocationId,
    onClose,
    source = 'detail',
  } = params
  const { template } = useSelector(selectSelectedTemplate)
  const services = useSelector(selectAllLeadOppProducts)

  const dispatch = useAppDispatch()

  const [formId, setFormId] = useState<number>()

  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const openEditDrawer = useCallback(() => setEditDrawerOpen(true), [])
  const closeEditDrawer = useCallback(() => {
    setEditDrawerOpen(false)
    onClose()
  }, [onClose])

  const { details } = useData(entityType)

  const locationRequired = useMemo(
    () =>
      template?.isCardCaptureEnabled && details?.externalLocationCode == null,
    [template, details?.externalLocationCode],
  )

  const validateAgreementForm = useCallback(
    async (model: CreateAgreementFormModel) => {
      const errors: FormikErrors<CreateAgreementFormModel> = {}

      if (template) {
        const { dynamicServiceBlock, numOfServices } = template

        const exceedsServiceSlots =
          !dynamicServiceBlock && services.length > numOfServices

        const distinctSvcLocs = new Set(
          services.map((s) => s.serviceLocationId),
        )

        const exceedsServiceLocations =
          !dynamicServiceBlock && distinctSvcLocs.size > 1

        if (
          exceedsServiceSlots ||
          exceedsServiceLocations ||
          locationRequired
        ) {
          errors.templateId = ''
        }
      }

      if (!model.templateId) {
        errors.templateId = 'Required'
      }

      return errors
    },
    [template, services, details, locationRequired],
  )

  const onSubmit = useCallback(
    async (model: CreateAgreementFormModel) => {
      const response = await post(
        `${ServiceConfig.baseAPIUrl}/Proposals/form/`,
        {
          templateId: model.templateId,
          formName: model.templateFormName,
          externalEntityType1: externalLocationId ? 'Location' : null,
          externalEntityId1: externalLocationId || null,
          externalEntityType5: model.entityType,
          externalEntityId5: model.entityId,
          appData: [{ key: 'includeDetail', value: model.includeDetail }],
          source,
        },
      )

      if (response.ok) {
        const json = await response.json()
        const res: AddFormResponse = json

        if (res.status == 'success') {
          setFormId(res.formId)

          if (entityType == ENTITY_TYPES.Lead) {
            dispatch(
              updateLeadAgreementsCount({
                action: 'increment',
                leadId: entityId,
              }),
            )
          } else {
            dispatch(
              updateOpportunityAgreementsCount({
                action: 'increment',
                oppId: entityId,
              }),
            )
          }

          openEditDrawer()
        }

        if (res.status == 'failed') {
          toast.error(res.statusMessage)
        }
      } else {
        toast.error(
          `Failed to add agreement to the selected ${entityType.toLowerCase()}.`,
        )
      }
    },
    [
      dispatch,
      externalLocationId,
      entityId,
      entityType,
      openEditDrawer,
      source,
    ],
  )

  return {
    formId,
    onSubmit,
    editDrawerOpen,
    closeEditDrawer,
    validateAgreementForm,
  }
}
